import React from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import { FlexiblePageInput } from '@/pages/types';
import { FlexiblePagePassword } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/FlexiblePagePassword';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { isSlideDeckRenderMode } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { AnalyticsPageView } from '../AnalyticsPageView';
import { FlexiblePageContentRenderer } from './FlexiblePageContentRenderer';
import { MadeWithMutinyWatermark } from './MadeWithMutinyWatermark';

type FlexiblePageRendererProps = {
  pageId: string;
};

export function FlexiblePageRenderer({ pageId }: FlexiblePageRendererProps) {
  const {
    componentTree,
    overrideVariables,
    password,
    passwordEnabled,
    renderStage,
    staticSiteDomain,
    bodySnippets,
    tailwindCss,
    customComponents,
    clientData,
    showMadeWithMutinyWatermark,
    isPdfExport,
    isThumbnail,
  } = useData<FlexiblePageInput>();
  const preloadedCustomComponents = usePageContext().preloadedCustomComponents;
  const isSlideDeck = isSlideDeckRenderMode(componentTree.data.renderMode);

  return (
    <>
      <AnalyticsPageView
        endpoint={clientData.analyticsEndpoint}
        companyId={clientData.analyticsCompanyId}
        assetId={clientData.analyticsAssetId}
        memberKey={clientData.analyticsMemberKey}
        clientApiToken={clientData.analyticsClientApiToken}
        recordSessionReplay={clientData.analyticsRecordSessionReplay}
        defaultOptOutPiiTracking={clientData.defaultOptOutPiiTracking}
        preview={clientData.preview}
      />
      {tailwindCss && <style data-tailwind-ssr dangerouslySetInnerHTML={{ __html: tailwindCss }} />}
      {bodySnippets && <div style={{ display: 'none' }} dangerouslySetInnerHTML={{ __html: bodySnippets }} />}
      <FlexiblePageContentRenderer
        componentTree={componentTree}
        overrideVariables={overrideVariables}
        renderStage={renderStage}
        staticSiteDomain={staticSiteDomain}
        customComponents={customComponents}
        isPdfExport={isPdfExport}
        isThumbnail={isThumbnail}
        preloadedCustomComponents={preloadedCustomComponents}
        stateConfig={
          clientData.componentStateEndpoint && clientData.componentStateCapability
            ? {
                endpoint: clientData.componentStateEndpoint,
                capability: clientData.componentStateCapability,
                defaultOptOutPiiTracking: clientData.defaultOptOutPiiTracking,
              }
            : undefined
        }
        sectionOverlay={
          showMadeWithMutinyWatermark && !isThumbnail && (isPdfExport || isSlideDeck) ? (
            <MadeWithMutinyWatermark position="absolute" />
          ) : undefined
        }
        afterContent={
          showMadeWithMutinyWatermark && !isSlideDeck && !isPdfExport && !isThumbnail ? (
            <MadeWithMutinyWatermark position="fixed" />
          ) : null
        }
      >
        {passwordEnabled && password && (
          <FlexiblePagePassword password={password} pageId={pageId} inPreview={renderStage === RenderStage.Preview} />
        )}
      </FlexiblePageContentRenderer>
    </>
  );
}
